import { requestInactiveBuyerBookings } from './requests/declined-bookings-requests';
import { BookingStatus, BookingReasonDeclinedLabel } from '../constants/securspace-constants';
import { formatDateMMDDyyyy, parseBookingTimestamp } from '../../util/DateUtils';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';

/**
 * Answers a buyer's "why was my booking declined".
 *
 * ## Why this is a hand-written skill and not a generated count
 *
 * The report skills answer "how many": they read `count` off a search and never
 * fetch a row. This question is the opposite — it needs the rows themselves, and
 * specifically the *reason* on each, so it fetches and inspects them.
 *
 * ## Why it exists at all
 *
 * Without it, "why was my booking declined" fell through to the backend agent,
 * which answered wrongly ("I cannot find any declined bookings") for an account
 * that plainly had one. A deterministic skill that reads the real rows cannot
 * make that mistake, and — the part the user actually cares about — it degrades
 * honestly when the supplier declined *without recording a reason*, which is the
 * common case, rather than pretending a reason exists or that no decline does.
 *
 * ## The N/A-reason case is the point
 *
 * `reasonDeclined` is frequently empty because the partner isn't forced to give
 * one. When it is, the skill says so plainly and points the buyer at the
 * supplier and at support, instead of surfacing a blank field or inventing a
 * cause. That is the whole reason it can be "more helpful" than the agent even
 * though there is no reason text to relay.
 */

/** The fields this skill reads off a booking row. Kept local and loose. */
interface DeclinedBookingRow {
  orderNumber?: string;
  locationName?: string;
  supplierCompanyName?: string;
  startDate?: string;
  endDate?: string;
  numberOfSpaces?: number;
  status?: string;
  creationDate?: string;
  reasonDeclined?: string;
  reasonDeclinedNote?: string;
}

const isDeclined = (row: DeclinedBookingRow): boolean =>
  (row.status ?? '').trim().toLowerCase() === BookingStatus.DECLINED.toLowerCase();

/** Newest first. Bad or missing timestamps sort last rather than throwing. */
const byCreatedDesc = (a: DeclinedBookingRow, b: DeclinedBookingRow): number => {
  const ta = parseBookingTimestamp(a.creationDate)?.getTime() ?? 0;
  const tb = parseBookingTimestamp(b.creationDate)?.getTime() ?? 0;
  return tb - ta;
};

const fmtDate = (value?: string): string => (value ? formatDateMMDDyyyy(value) : '');

const dateRange = (row: DeclinedBookingRow): string => {
  const start = fmtDate(row.startDate);
  const end = fmtDate(row.endDate);
  if (start && end) return `${start} \u2013 ${end}`;
  return start || end || 'unspecified dates';
};

const spacesPhrase = (row: DeclinedBookingRow): string => {
  const n = row.numberOfSpaces;
  if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
    return `${n} ${n === 1 ? 'space' : 'spaces'}`;
  }
  return 'space';
};

const bookingLabel = (row: DeclinedBookingRow): string => {
  const at = row.locationName ? ` at ${row.locationName}` : '';
  const from = row.supplierCompanyName ? ` (${row.supplierCompanyName})` : '';
  return row.orderNumber ? `#${row.orderNumber}${at}${from}` : `Booking${at}${from}`;
};

/**
 * The reason a booking was declined, in one sentence — or the honest fallback
 * when the supplier recorded none. `reasonDeclined` holds an enum whose label
 * lives in {@link BookingReasonDeclinedLabel}; an unmapped value is treated as
 * "no usable reason" rather than shown raw.
 */
const describeReason = (row: DeclinedBookingRow): string => {
  const label = row.reasonDeclined
    ? (BookingReasonDeclinedLabel as Record<string, string>)[row.reasonDeclined]
    : undefined;
  const note = row.reasonDeclinedNote?.trim();

  if (label && note) return `The supplier's stated reason was **${label}** \u2014 \u201c${note}\u201d.`;
  if (label) return `The supplier's stated reason was **${label}**.`;
  if (note) return `The supplier's note was: \u201c${note}\u201d.`;

  const who = row.supplierCompanyName ? `the supplier (**${row.supplierCompanyName}**)` : 'the supplier';
  return (
    `The supplier didn't record a reason when declining it, so there's nothing on the ` +
    `booking that tells you why. The quickest way to find out is to contact ${who} directly; ` +
    `you can also reach SecurSpace support and we'll help follow up.`
  );
};

const answer = async (): Promise<KnowledgeAnswer> => {
  const response = await requestInactiveBuyerBookings();
  const rows = (response?.body?.content ?? []) as DeclinedBookingRow[];
  const declined = rows.filter(isDeclined).sort(byCreatedDesc);

  if (declined.length === 0) {
    return {
      text:
        "I couldn't find any declined bookings on your account. If a booking you expected " +
        'is missing, it may be cancelled or expired rather than declined \u2014 those show ' +
        'under Inactive too \u2014 or reach out to SecurSpace support and we can take a look.',
    };
  }

  const latest = declined[0];
  const lead =
    declined.length === 1
      ? 'Your declined booking is'
      : 'Your most recent declined booking is';

  let text = `${lead} **${bookingLabel(latest)}** \u2014 ${spacesPhrase(latest)} for ${dateRange(latest)}. ${describeReason(latest)}`;

  if (declined.length > 1) {
    const others = declined.length - 1;
    text += ` You have ${others} other declined booking${others === 1 ? '' : 's'}; they're listed below.`;
    return {
      text,
      breakdown: declined.slice(1, 11).map((row) => {
        const label = row.reasonDeclined
          ? (BookingReasonDeclinedLabel as Record<string, string>)[row.reasonDeclined]
          : undefined;
        return { label: bookingLabel(row), value: label ?? 'No reason recorded' };
      }),
    };
  }

  return { text };
};

const SYNONYMS = [
  'why was my booking declined',
  'why was my booking rejected',
  'why was my booking denied',
  'why did my booking get declined',
  'why did my booking get rejected',
  'why did my booking get denied',
  'why did they decline my booking',
  'why was i declined',
  'why was my request declined',
  'why was my reservation declined',
  'reason my booking was declined',
  'reason for my declined booking',
  'my booking was declined',
  'my booking got declined',
  'booking declined reason',
];

/**
 * Fallback concepts for phrasings the synonyms miss. Two narrow groups: a
 * decline word and a booking word. A count question like "how many bookings
 * were declined" is caught first by the booking-count skill's synonym, so this
 * only fires for the diagnostic phrasing that has no count word.
 */
const REQUIRES = [
  ['declined', 'decline', 'rejected', 'reject', 'denied', 'deny'],
  ['booking', 'bookings', 'reservation', 'request'],
];

/**
 * Buyer-only. A supplier declines bookings rather than having theirs declined,
 * so "why was my booking declined" is a buyer's question; a supplier asking
 * about a decline is asking something this skill does not answer.
 */
export const whyBookingDeclinedQuery: KnowledgeQuery = {
  id: 'buyerWhyBookingDeclined',
  audiences: ['buyer'],
  synonyms: SYNONYMS,
  requires: REQUIRES,
  example: 'why was my booking declined',
  description:
    "Explains why a buyer's booking was declined. Finds the buyer's declined bookings and " +
    "reports the supplier's stated reason for the most recent one, or \u2014 when the supplier " +
    'recorded no reason \u2014 says so plainly and suggests contacting the supplier or support. ' +
    'A diagnostic lookup for a specific booking outcome, not a count of bookings and not a ' +
    'money figure.',
  answer: (_context: KnowledgeContext): Promise<KnowledgeAnswer> => answer(),
};

export const __testing = {
  describeReason,
  byCreatedDesc,
  isDeclined,
  bookingLabel,
};
